import axios, { CancelTokenSource } from "axios"
import { removeDuplicatesInFilter } from "Components/layout/partials/navbar/activity/templates/utils"
import * as HttpStatus from "http-status"
import i18n from "Shared/locales/i18n"
import { AuthService, LocalStorageService, NotificationService } from "Shared/services"
import { WEAVR_TOKEN } from "Shared/services/localstorage.service"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import store from "Shared/store"
import { logout, setIsWeavrAuthenticated, setIsWeavrUserOnboard } from "Shared/store/actions/auth/authActions"
import { WeavrAuthenticatedEnum, WeavrUserOnboardEnum } from "Shared/store/reducers/authReducer"

class AxiosInterceptor {
    private failedQueue: any = []
    private weavrFailedQueue: any = []
    private cancelSource: CancelTokenSource
    private isRefreshing = false
    private isWeavrAuthenticated = false

    constructor() {
        this.cancelSource = axios.CancelToken.source()
        axios.defaults.baseURL = process.env.REACT_APP_API_URL
        axios.defaults.withCredentials = true
        // Make sure axios is not blocking for ever by setting a timeout of 60 sec (value is specificed in ms hence * 1000)
        // default timeout is 0
        if (process.env.DEPLOY_ENVIRONMENT !== "localhost") axios.defaults.timeout = 1000 * 60
    }

    setupInterceptors() {
        this.setupRequestInterceptor()
        this.setupResponseInterceptor()
    }

    // this should be called right after the login
    setNewCancelToken = () => (this.cancelSource = axios.CancelToken.source())

    getHeader = () => ({
        ...LocalStorageService.getAuthenticationHeader(),
        "Content-Type": "application/json",
        "x-language": i18n.language,
        "x-timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    private setupRequestInterceptor() {
        axios.interceptors.request.use(
            async (config: any) => {
                if (LocalStorageService.isAuthorized()) {
                    config.headers = this.getHeader()
                }

                this.setNewCancelToken()
                config.cancelToken = this.cancelSource.token
                config.url = removeDuplicatesInFilter(config.url)

                return config
            },
            (error) => Promise.reject(error)
        )
    }

    private processQueue = (error: any, isWeavrQueue = false) => {
        console.clear()

        const queue = isWeavrQueue ? this.weavrFailedQueue : this.failedQueue
        let isResolved = true
        queue.reverse().forEach((prom: any) => {
            if (error) {
                isResolved = false
                prom.reject(error)
            } else {
                prom.resolve()
            }
        })

        console.clear()

        if (queue.length > 0 && !isResolved) {
            this.cancelSource.cancel("cancelled")
        }

        isWeavrQueue ? (this.weavrFailedQueue = []) : (this.failedQueue = [])
    }

    private handleLogout = async (error: Error) => {
        this.processQueue(error)
        await store.dispatch(logout())
        this.cancelSource.cancel("expired")
    }

    private handleSilentRefresh = async (originalRequest: any) => {
        const self = this
        if (this.isRefreshing) {
            return new Promise(function (resolve, reject) {
                self.failedQueue.push({ resolve, reject })
            })
                .then(() => {
                    originalRequest.headers = this.getHeader()
                    return axios(originalRequest)
                })
                .catch((err) => {
                    return Promise.reject(err)
                })
        }

        originalRequest._retry = true
        this.isRefreshing = true
        return new Promise(function (resolve, reject) {
            AuthService.silentRefresh()
                .then(() => {
                    axios.defaults.cancelToken = self.cancelSource.token
                    resolve(axios(originalRequest))
                })
                .catch((error) => {
                    self.isRefreshing = false
                    self.handleLogout(error)
                    reject(error)
                })
                .finally(() => {
                    self.isRefreshing = false
                    self.processQueue(null)
                })
        })
    }

    private handleWeavrAuthentication = async (isAuthenticated: boolean, originalRequest?: any) => {
        const self = this
        // handle authentication status, help open pin modal if user is onboarded
        await store.dispatch(setIsWeavrAuthenticated(!isAuthenticated ? WeavrAuthenticatedEnum.DISCONNECTED : WeavrAuthenticatedEnum.CONNECTED))

        // Open onboarding modal in case user is only enrolled
        const isUserUpdateRequest = originalRequest?.url?.startsWith("/users/") && originalRequest.method === "put"
        await store.dispatch(setIsWeavrUserOnboard(!isAuthenticated && !isUserUpdateRequest ? WeavrUserOnboardEnum.NOT_ONBOARD : WeavrUserOnboardEnum.ONBOARD))

        if (!this.isWeavrAuthenticated) {
            const request = new Promise(function (resolve, reject) {
                self.weavrFailedQueue.push({ resolve, reject })
            })
                .then(() => {
                    originalRequest.headers = self.getHeader()
                    return axios(originalRequest)
                })
                .catch((err) => Promise.reject(err))

            return request
        }

        return new Promise(function (_resolve, reject) {
            try {
                axios.defaults.cancelToken = self.cancelSource.token

                self.processQueue(null, true)
                self.isWeavrAuthenticated = true
            } catch (error) {
                self.isWeavrAuthenticated = false
                reject(error)
            }
        })
    }

    public cancelWeavrRequests = () => {
        this.isWeavrAuthenticated = false
        this.processQueue("cancelled", true)
    }

    private setupResponseInterceptor() {
        axios.interceptors.response.use(
            async (response) => {
                if (response && response.config.url && response.config.url?.indexOf("/logout") > -1) {
                    this.cancelSource.cancel("logout")
                }

                if (response && response.config.url && response.config.url?.includes("/corporate/user/login")) {
                    LocalStorageService.setWeavrToken(response.data.accessToken)
                    this.isWeavrAuthenticated = true
                    this.handleWeavrAuthentication(true)
                }

                return response
            },
            async (error) => {
                const originalRequest = error?.config

                if (!error?.config || !error.config.url) {
                    return Promise.reject(error)
                }

                if (error.config.url === "/login") {
                    return Promise.reject(error)
                }

                if (error.config.url === "/token/refresh" || (error.response && error.response.message === i18n.t("error:auth.account_suspended"))) {
                    if (error.config.url === "/token/refresh") {
                        NotificationService.send(NotificationTypeEnum.WARNING, i18n.t("notification:session_expired.title"), i18n.t("notification:session_expired.message"), 0)
                    }
                    await this.handleLogout(error)
                    return Promise.reject(error)
                }

                if (error && error.response) {
                    const { status, data } = error.response
                    if (status === HttpStatus.UNAUTHORIZED && !originalRequest._retry) {
                        const silentRefresh = this.handleSilentRefresh(originalRequest)
                        originalRequest._retry = false
                        return silentRefresh
                    }
                    // prettier-ignore
                    if (status === HttpStatus.UNPROCESSABLE_ENTITY && data.error && data.error.toLowerCase().includes(i18n.t('error:auth.not_authenticated').toLowerCase()) && !originalRequest._retry) {
                        this.isWeavrAuthenticated = false;
                        localStorage.removeItem(WEAVR_TOKEN);
                        return this.handleWeavrAuthentication(false, originalRequest);
                    }
                }

                return Promise.reject(error)
            }
        )
    }
}

export default new AxiosInterceptor()
